/**
 * Live range / expected arrival SoC from pack energy + running leg efficiency.
 *
 * usable_kWh = capacity × (SoC − reserve) / 100
 * kWh/km     = this leg's own (discharge − regen) / distance -- computed server-side from
 *              the leg's own start-of-leg baseline, see progress.leg_kwh_per_km -- else the
 *              fleet's measured average for the direction. Never a constant.
 * expected SoC at destination = SoC − (km_left × kWh/km / capacity) × 100
 */
import type { InboundRow, RouteProgress, TimsTrip, Vehicle, VehicleMeta } from './types'
import { isLaden } from '../map/layers/vehicles'

/** Matches the dispatch floor in db/reports_soh_range.sql. Two different reserves for one
 *  fleet -- 8 here and 12 there -- meant the map and the report quoted different ranges for
 *  the same truck, and neither was wrong on its own terms. */
export const RESERVE_SOC_PCT = 12
export const MIN_LEG_KM = 12
export const RANGE_RISK_SOC_PCT = 20

/**
 * How far into a leg a truck must be before its range is worth judging.
 *
 * A TRUCK STILL AT THE PLANT HAS NOT CHARGED YET. MH18BZ3034 sat at Dhar on 28% SoC with
 * km_along 1.485, stationary, and the console announced it would not reach Jhulwania 91 km
 * away. Perfectly true of the pack at that instant and completely useless: it had not
 * departed, and it does not depart on the state of charge it happens to hold while waiting.
 * Every truck at a plant reads as a range emergency under that rule, which is how a warning
 * that matters gets ignored.
 *
 * 10 km is measured from the corridor rather than picked: Dhar's six fences span about 2 km,
 * and trucks genuinely waiting there sit at km_along 0.2-1.7 while one actually rolling
 * reads 8.4 and climbing. Ten clears the whole site with margin, and lands just past Bakaner
 * bridge -- the first waypoint that means a truck has committed to the run.
 *
 * km_along is measured from the origin of whichever route the truck is on, so this works
 * unchanged in both directions: at Dhule on the return leg it suppresses exactly the same
 * way, with no per-site special case.
 */
export const MIN_KM_UNDER_WAY = 10
/**
 * NO HARDCODED EFFICIENCY. Two constants lived here -- 2.2 loaded and 1.1 empty -- used
 * whenever a truck had no figure of its own, which is most of the time early in a leg. They
 * happened to sit close to the measured values (2.131 and 1.175), and that is the most
 * dangerous property a guess can have: being right once buys it trust it has not earned,
 * and it will not follow the fleet when tyres, load, season or route change it.
 *
 * Efficiency is now MEASURED or it is ABSENT. In order:
 *
 *   1. this truck's own current leg, once it has covered enough ground to have one
 *   2. the fleet's measured average for the direction it is going, from v_efficiency_by_load
 *
 * If neither exists the range is null and the console shows nothing. A dispatcher who sees
 * no range knows to go and find out; one who sees a plausible invented number does not.
 */

/** Measured fleet efficiency, as fetched from the reports API. Absent until it loads. */
export interface FleetEfficiency {
  laden?: number | null
  empty?: number | null
  /**
   * The LADEN back half -- jhulwania-depot to Dhule -- measured on its own, see
   * v_efficiency_remaining_by_load. `laden` above is a whole-leg (Dhar to Dhule) average,
   * and every split point checked south of the plant read well under it (1.0-1.5 kWh/km
   * against a 2.131 whole-leg figure): the expensive driving on this corridor concentrates
   * in the first half, so charging the whole-leg average against what is left once a truck
   * is past Jhulwania overstates the energy it actually needs. `splitLat` is
   * jhulwania-depot's own latitude: a truck at or south of it (LADEN runs Dhar -> Dhule,
   * decreasing latitude) is in the measured back half.
   * EMPTY has no equivalent -- nothing here measures whether the return leg's back half (the
   * Dhar-ward one) has the same property, so none is claimed for that direction.
   */
  ladenRemaining?: { kwhPerKm: number; splitLat: number } | null
}

/**
 * The band a leg's efficiency has to fall in to be believed, matching v_efficiency_by_load.
 *
 * WHY THIS STILL EXISTS NOW THAT THE LEG FIGURE IS SERVER-COMPUTED. rules.trips.live_energy
 * already guards the arithmetic that used to produce 0.031 kWh/km for MH18BZ3109 (a "range
 * left ~6555 km" that no dispatcher could act on) -- a negative counter difference or regen
 * exceeding discharge now reports no figure at all, not a wrong one. This band is what is
 * left to check on the client: a real, correctly-computed figure that is still not physically
 * possible for a 55 T unit on this corridor is a sign something upstream of the guard is
 * wrong (a bad odometer unit, a misconfigured capacity), and the honest response is still to
 * show no range rather than an implausible one.
 *
 * 0.6 to 4.0: an empty 20 T unit rolling downhill does not beat 0.6, and a laden 55 T unit
 * on the Dhar ghat does not exceed 4.0. Measured legs land at 0.9-1.2 empty and 2.0-2.5
 * laden, so the band is wide enough to be a sanity check rather than a filter.
 */
export const MIN_PLAUSIBLE_KWH_PER_KM = 0.6
export const MAX_PLAUSIBLE_KWH_PER_KM = 4.0

export interface RangeEstimate {
  usable_kwh: number | null
  kwh_per_km: number | null
  /** Which answer was used, so a reader can tell a measurement from a fallback: this
   *  truck's own current leg, the fleet's whole-leg average, or the fleet's average for
   *  just the remaining (post-ghat, LADEN-only) segment. */
  kwh_per_km_source: 'leg' | 'fleet' | 'fleet_remaining' | null
  km_remaining: number | null
  est_range_km: number | null
  /** Never below zero. A state of charge cannot be negative -- see the note on `shortfall_km`. */
  expected_soc_pct: number | null
  /**
   * How far short of the destination the truck runs out, or null if it gets there.
   *
   * THIS REPLACES A NEGATIVE expected_soc_pct. The console showed "exp SoC -6.7% at JHUL",
   * which is not a state of charge -- it is the arithmetic running past empty and being
   * printed as though a pack could hold less than nothing. The underlying claim was real and
   * serious (that truck could not reach Jhulwania) but the number carrying it was nonsense,
   * so it read as a bug in the calculation and invited exactly the wrong response: distrust
   * the figure rather than charge the truck.
   *
   * Expected SoC is now floored at 0 -- what the pack will actually be -- and the part that
   * used to go negative is stated as the distance it comes up short.
   */
  shortfall_km: number | null
  margin_km: number | null
  /** Expected SoC at next destination below RANGE_RISK_SOC_PCT. */
  range_risk: boolean
}

/**
 * estimateRange's own km_remaining comes from the live progress map, which can be stale,
 * for a different leg, or simply missing -- while arrival_board's km_to_go is exactly the
 * distance to THIS row's hub, computed fresh every 20s. Overrides it rather than trust
 * progress's, so the map popup and the Arrivals board never quote two different distances
 * (and so two different expected-SoC figures) for the same truck. Everything else (leg
 * efficiency so far, under-way gate) still comes from live progress when it exists.
 */
export function overrideProgressWithArrival(
  vehicleId: string,
  base: RouteProgress | undefined,
  row: InboundRow | undefined,
  now: number,
): RouteProgress | undefined {
  if (!row) return base
  // Spread base FIRST so every field it carries -- including ones added after this
  // function was written (trip_id/origin_hub/started_ts) -- rides along by construction.
  // Hand-copying field-by-field is how route_length_km went missing here before, silently.
  return {
    ...base,
    vehicle_id: vehicleId,
    route_id: base?.route_id ?? null,
    ts: base?.ts ?? now,
    km_along: base?.km_along ?? null,
    km_remaining: row.km_to_go,
    next_hub: row.hub_code,
    next_hub_code: row.hub_code,
    km_to_next_hub: row.km_to_go,
    eta_ts: row.measured_eta_min != null ? now + row.measured_eta_min * 60_000 : null,
    eta_min: row.measured_eta_min ?? null,
    expected_soc_pct: row.expected_soc_pct ?? null,
    forecast_confidence: row.confidence,
    forecast_km_to_go: row.km_to_go,
    off_corridor_m: base?.off_corridor_m ?? null,
    route_bearing_deg: base?.route_bearing_deg ?? null,
    diverted: base?.diverted ?? false,
  }
}

export function estimateRange(
  v: Vehicle,
  meta: VehicleMeta | undefined,
  progress: RouteProgress | undefined,
  now = Date.now(),
  trip?: TimsTrip,
  /** The fleet's measured kWh/km by load. Preferred over the hardcoded prior. */
  fleet?: FleetEfficiency,
): RangeEstimate {
  void now
  // No fallback constant. A hardcoded pack size silently disagreeing with the register
  // produces a range estimate that is confidently wrong -- and a dispatcher routing a
  // truck on it has no way to tell. If capacity is unknown, say so by returning null.
  // THE PACK IT HAS, not the pack it shipped with. soh_pct comes from the BMS via
  // vehicle_soh_daily; at 95% a 282 kWh nameplate is 268 kWh, and quoting the nameplate
  // overstates range by exactly the degradation -- worst on the oldest trucks, which are
  // the ones most likely to be stranded. Falls back to nameplate when SoH is unknown.
  const soh = meta?.soh_pct
  const capacity = meta?.capacity_kwh != null && soh != null && soh > 0 && soh <= 110
    ? meta.capacity_kwh * (soh / 100)
    : meta?.capacity_kwh
  const soc = v.soc_pct
  const empty: RangeEstimate = {
    usable_kwh: null, kwh_per_km: null, kwh_per_km_source: null,
    km_remaining: progress?.km_remaining ?? null,
    est_range_km: null, expected_soc_pct: null, shortfall_km: null,
    margin_km: null, range_risk: false,
  }
  if (soc == null || capacity == null || capacity <= 0) return empty

  const usableFrac = Math.max(0, (soc - RESERVE_SOC_PCT) / 100)
  const usable_kwh = round(capacity * usableFrac, 2)

  let kwh_per_km: number | null = null
  let source: 'leg' | 'fleet' | 'fleet_remaining' | null = null

  /**
   * THE SESSION COUNTERS ON THE VEHICLE RECORD ARE NOT RESET AT A HUB -- monotonic for the
   * vehicle's whole life in the state store, by design (see snapshot.py's ENERGY_EPOCH note),
   * because a leg's energy is a difference between two readings of them. Reading them
   * DIRECTLY here, the way this used to work, meant guessing whether a huge cumulative total
   * plausibly belonged to a leg this short.
   *
   * MH18BZ2874, laden and 6.1 km into the Dhar->Jhulwania run, carried 130 km of session
   * distance -- the empty return leg it had just finished. MH18BZ2871, 110 km into a laden
   * leg, carried 179. Neither is a leg efficiency; both are the wrong difference.
   *
   * progress.leg_kwh_per_km is the RIGHT difference, computed server-side in
   * rules.trips.live_energy from the same start-of-leg baseline that already makes the
   * closed trip's kwh_per_km correct -- captured once at the leg's first snapshot, not
   * reconstructed here from a guess about how far into the leg the truck probably is.
   */
  const legKm = progress?.leg_km ?? null
  const legEfficiency = legKm != null && legKm >= MIN_LEG_KM
    ? (progress?.leg_kwh_per_km ?? null)
    : null
  // Still worth a sanity band even though the source is now authoritative: a live figure
  // read mid-tick, before enough of the leg's ground truth has accumulated, is a real
  // number rather than a guess, but "real" and "physically possible" are not the same test.
  if (legEfficiency != null
      && legEfficiency >= MIN_PLAUSIBLE_KWH_PER_KM
      && legEfficiency <= MAX_PLAUSIBLE_KWH_PER_KM) {
    kwh_per_km = legEfficiency
    source = 'leg'
  } else if (
    isLaden(meta, trip, progress) && fleet?.ladenRemaining != null
    && v.lat != null && v.lat <= fleet.ladenRemaining.splitLat
  ) {
    // MH18BZ2871: 82% SoC, 89.6 km left, past Jhulwania -- and the whole-leg average said
    // it would run out 2.7 km short. That average is dominated by the corridor's first
    // half; measured from this same position, 15 real legs spend 1.76 kWh/km getting to
    // Dhule, not the corridor's 2.13. At or south of the split this measured figure is
    // used INSTEAD OF, not blended with, the whole-leg one -- the whole-leg number is not
    // "roughly right" for this stretch, it is answering a different question (the average
    // over ground this truck has already covered).
    kwh_per_km = fleet.ladenRemaining.kwhPerKm
    source = 'fleet_remaining'
  } else {
    // THE FLEET'S OWN MEASURED FIGURE BEFORE THE GUESS. A truck early in its leg has no
    // efficiency of its own yet, but the fleet has 29 completed legs and knows what a
    // loaded run costs on this corridor. Falling straight to a constant threw that away.
    const measured = isLaden(meta, trip, progress) ? fleet?.laden : fleet?.empty
    if (measured != null && measured > 0) {
      kwh_per_km = measured
      source = 'fleet'
    }
    // and otherwise NOTHING. kwh_per_km stays null, so range and expected SoC stay null too.
    // Absent is the honest answer when nothing has been measured.
  }

  let km_remaining = progress?.km_remaining ?? progress?.km_to_next_hub ?? null
  if (km_remaining == null && progress?.km_along != null && progress.route_length_km != null) {
    km_remaining = Math.max(0, progress.route_length_km - progress.km_along)
  }
  const est_range_km = kwh_per_km && kwh_per_km > 0
    ? round(usable_kwh / kwh_per_km, 1)
    : null

  let expected_soc_pct: number | null = null
  let margin_km: number | null = null
  let shortfall_km: number | null = null
  if (km_remaining != null && kwh_per_km != null && kwh_per_km > 0) {
    const burn = (km_remaining * kwh_per_km / capacity) * 100
    const raw = soc - burn
    // Floored at zero: this is what the pack will HOLD, and a pack cannot hold less than
    // nothing. The overshoot is not discarded -- it becomes the distance below.
    expected_soc_pct = round(Math.max(0, raw), 1)
    margin_km = est_range_km != null ? round(est_range_km - km_remaining, 1) : null
    // The truck runs out before it arrives. Said in kilometres, which is what a dispatcher
    // decides on -- a recovery vehicle goes to a place, not to a percentage.
    if (est_range_km != null && est_range_km < km_remaining) {
      shortfall_km = round(km_remaining - est_range_km, 1)
    }
  }

  // NOT UNDER WAY YET, so there is nothing to judge. Either the truck has not cleared the
  // origin site, or it is plugged in and its state of charge is still rising. In both cases
  // the number this would warn about is not the number it departs on.
  //
  // The estimate itself is still returned -- a dispatcher looking at a truck at the plant
  // should see its range and its efficiency. What is withheld is the ALERT, because an
  // alert asserts that something needs doing and nothing does.
  // power_kw < 0 is also regen braking while moving -- gate on !moving so a truck well
  // under way that regen-brakes once doesn't get its range-risk alert suppressed as if
  // it were still plugged in at the origin. Same gate vehicleState() (format.ts) uses
  // for the identical reason.
  const charging = (v.charger_state != null && v.charger_state > 0)
    || (!v.moving && (v.speed_kph == null || v.speed_kph < 1) && v.power_kw != null && v.power_kw < 0)
  const under_way = (progress?.km_along ?? Infinity) >= MIN_KM_UNDER_WAY
  if (!under_way || charging) {
    return {
      usable_kwh, kwh_per_km: kwh_per_km != null ? round(kwh_per_km, 3) : null,
      kwh_per_km_source: source, km_remaining, est_range_km,
      expected_soc_pct, shortfall_km: null, margin_km, range_risk: false,
    }
  }

  // A truck that cannot reach the hub is at risk whatever its floored SoC reads. Testing the
  // floored value alone would have let 0.0% -- the worst case there is -- look like a pass on
  // any threshold above zero.
  const range_risk = shortfall_km != null
    || (expected_soc_pct != null && expected_soc_pct < RANGE_RISK_SOC_PCT)

  return {
    usable_kwh, kwh_per_km: kwh_per_km != null ? round(kwh_per_km, 3) : null,
    kwh_per_km_source: source, km_remaining,
    est_range_km, expected_soc_pct, shortfall_km, margin_km, range_risk,
  }
}

/**
 * The corridor forecast, when the server has attached one, replaces this function's
 * own SoC and kilometres. Both numbers are the SQL walk; a second arithmetic here
 * would put a different arrival on the map from the one on the board.
 */
export function withCorridorForecast(
  v: { charger_state: number | null; moving: boolean; speed_kph: number | null; power_kw?: number | null },
  range: RangeEstimate,
  progress?: RouteProgress,
): RangeEstimate {
  if (!progress || progress.forecast_confidence == null) return range
  const charging = (v.charger_state != null && v.charger_state > 0)
    || (!v.moving && (v.speed_kph == null || v.speed_kph < 1) && v.power_kw != null && v.power_kw < 0)
  const underWay = (progress.km_along ?? Infinity) >= MIN_KM_UNDER_WAY
  const km = progress.forecast_km_to_go ?? range.km_remaining
  const expected = progress.expected_soc_pct ?? null
  const shortfall = !charging && underWay ? (progress.shortfall_km ?? null) : null
  const est = progress.est_range_km ?? null
  const source = progress.forecast_kwh_source ?? null
  return {
    ...range,
    km_remaining: km,
    kwh_per_km: progress.forecast_kwh_per_km ?? null,
    kwh_per_km_source: source,
    est_range_km: est,
    expected_soc_pct: expected,
    shortfall_km: shortfall,
    margin_km: est != null && km != null ? round(est - km, 1) : null,
    range_risk: !charging && underWay && (
      shortfall != null || (expected != null && expected < RANGE_RISK_SOC_PCT)
    ),
  }
}

function round(n: number, d: number): number {
  const f = 10 ** d
  return Math.round(n * f) / f
}
